/**
 * Sentry wiring for the FE.
 *
 * No-op unless:
 *   - ``VITE_SENTRY_DSN`` is set at build time (Vercel env), AND
 *   - the user has granted ``analytics`` consent in the cookie
 *     banner (PR 05 of the GDPR/ISO27001/NIS2 readiness plan).
 *
 * The consent gate is the structurally important part: without it
 * we'd start sending performance / error events before the visitor
 * had a chance to opt out, which is exactly the violation
 * ``test_sentry_blocked_when_analytics_declined`` exists to prevent.
 *
 * We dynamically import ``@sentry/react`` so environments without the
 * DSN never pay the ~40 kB bundle cost — Vite code-splits this into a
 * separate chunk that's never fetched in that case.
 */

import { getConsentCategory } from "@/lib/consent";

/** Běží v téhle záložce klient Sentry? Brání dvojímu initu a řídí odvolání. */
let started = false;

export async function initSentry(): Promise<void> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return;

  // Consent gate — the user MUST have actively granted analytics for
  // us to load Sentry's client. Default-off; a later change in the
  // banner goes through ``applyAnalyticsConsent``.
  if (!getConsentCategory("analytics")) return;
  if (started) return;

  try {
    const Sentry = await import("@sentry/react");
    started = true;
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: `dxf-web@${import.meta.env.VITE_APP_VERSION ?? "dev"}`,
      // 10 % sampling in production, 0 otherwise.
      tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
      sendDefaultPii: false,
    });
  } catch (err) {
    started = false;
    // eslint-disable-next-line no-console
    console.warn("sentry: init failed", err);
  }
}

/**
 * Promítne změnu souhlasu z cookie lišty hned, ne až po dalším načtení
 * stránky (KAN-1939): udělený souhlas Sentry spustí, odvolaný ho
 * zastaví.
 */
export async function applyAnalyticsConsent(granted: boolean): Promise<void> {
  if (granted) {
    await initSentry();
    return;
  }
  if (!started) return;
  started = false;
  try {
    const Sentry = await import("@sentry/react");
    await Sentry.close();
  } catch {
    // Zastavení selhalo. Po dalším načtení se Sentry stejně nespustí,
    // protože souhlas už v prohlížeči uložený není.
  }
}

/**
 * Report an error and hand back the Sentry event id, which the error
 * screen shows the customer as a support code.
 *
 * Returns ``undefined`` whenever Sentry isn't actually running — no
 * DSN, or analytics consent not granted. That's the honest answer: an
 * id for an event nobody received would send support hunting for a
 * record that doesn't exist.
 */
export async function captureError(
  error: unknown,
  componentStack?: string,
): Promise<string | undefined> {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (!dsn) return undefined;
  if (!getConsentCategory("analytics")) return undefined;

  try {
    const Sentry = await import("@sentry/react");
    return Sentry.captureException(
      error,
      componentStack ? { contexts: { react: { componentStack } } } : undefined,
    );
  } catch {
    // A Sentry-side hiccup must never mask the original error — the
    // caller still has an error screen to render.
    return undefined;
  }
}
